<template>
    <div class="cold-client-statistics">
        <!-- Информационное сообщение -->
        <div class="alert alert-info mb-5">
            <div class="d-flex align-items-start">
                <div>
                    <h6 class="alert-heading mb-2">О системе холодных клиентов</h6>
                    <p class="mb-0">
                        Ежемесячно мы предоставляем вам набор клиентов из вашего региона. 
                        Новые контакты выдаются только после полной обработки текущих. 
                        Помните, это дополнительная помощь в поиске клиентов, а не единственный источник.
                    </p>
                </div>
            </div>
        </div>

        <!-- Статистика по статусам -->
        <div class="statistics-section">
            <h5 class="mb-3 ml-4">
                <i class="bx bx-bar-chart-alt-2 me-2"></i>
                Статистика по холодным клиентам
            </h5>
            
            <div v-if="loading" class="text-center py-4">
                <b-spinner variant="primary"></b-spinner>
                <p class="mt-2 text-muted">Загрузка статистики...</p>
            </div>

            <div v-else-if="statistics" class="statistics-content">
                <!-- Статистика по статусам -->
                <div class="status-statistics">
                    <div class="row g-3">
                        <div 
                            v-for="(count, status) in statistics.by_status" 
                            :key="status"
                            class="col-lg-4 col-md-6 col-12 mb-2"
                        >
                            <div class="status-card" :class="getStatusCardClass(status)">
                                <div class="status-card-body">
                                    <div class="status-icon-wrapper">
                                        <i :class="getStatusIcon(status)"></i>
                                    </div>
                                    <div class="status-content">
                                        <div class="status-label">{{ getStatusLabel(status) }}</div>
                                        <div class="status-count">{{ count }}</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Информация о следующих контактах -->
                <div class="next-contacts-info mt-5">
                    <!-- Случай когда все контакты обработаны -->
                    <div v-if="isAllContactsProcessed" class="alert alert-success">
                        <div class="d-flex align-items-start">
                            <div>
                                <h6 class="alert-heading mb-2 text-success">
                                    <i class="bx bx-trophy me-2"></i>
                                    Поздравляем! Все контакты обработаны
                                </h6>
                                <p class="mb-2">
                                    Вы отлично поработали и обработали всех клиентов из текущей группы. 
                                    В следующем периоде вы обязательно получите новую группу контактов для работы.
                                </p>
                                <div class="success-message">
                                    <i class="bx bx-star me-1"></i>
                                    <strong>Отличная работа! Продолжайте в том же духе!</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                    
                    <!-- Случай когда есть необработанные контакты -->
                    <div v-else class="alert alert-warning">
                        <div class="d-flex align-items-start">
                            <div>
                                <h6 class="alert-heading mb-2">Получение следующей группы контактов</h6>
                                <p class="mb-2">
                                    Для получения новых контактов в следующем периоде необходимо обработать всех клиентов 
                                    со статусом <strong>"Не обработан"</strong>.
                                </p>
                                <div class="remaining-clients">
                                    <span class="remaining-label">Осталось обработать:</span>
                                    <span class="remaining-count">{{ statistics.by_status ? statistics.by_status.new : 0 }} клиентов</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div v-else class="text-center py-4">
                <i class="bx bx-error-circle text-muted" style="font-size: 2rem;"></i>
                <p class="mt-2 text-muted">Не удалось загрузить статистику</p>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { API_COLD_CLIENTS } from '@/constants/api';

export default {
    name: 'ColdClientStatistics',
    data() {
        return {
            statistics: null,
            loading: false,
            error: null
        }
    },
    computed: {
        ...mapGetters('auth', ['currentUser']),
        
        isAllContactsProcessed() {
            if (!this.statistics || !this.statistics.by_status) {
                return false;
            }
            // Считаем, что все контакты обработаны, если нет клиентов со статусом "new"
            return this.statistics.by_status.new === 0;
        }
    },
    mounted() {
        this.loadStatistics();
    },
    methods: {
        async loadStatistics() {
            this.loading = true;
            this.error = null;
            
            try {
                const response = await api.call('get', `${API_COLD_CLIENTS}/statistics`);
                
                if (response.data.success) {
                    this.statistics = response.data.data;
                } else {
                    this.error = 'Ошибка при загрузке статистики';
                }
            } catch (error) {
                console.error('Error loading statistics:', error);
                this.error = 'Ошибка при загрузке статистики';
            } finally {
                this.loading = false;
            }
        },
        
        getStatusLabel(status) {
            const labels = {
                'new': 'Не обработаны',
                'contacted': 'Связались',
                'interested': 'Заинтересованы',
                'not_interested': 'Не заинтересованы',
                'no_answer': 'Не дозвонились'
            };
            return labels[status] || status;
        },
        
        getStatusClass(status) {
            const classes = {
                'new': 'badge-secondary',
                'contacted': 'badge-info',
                'interested': 'badge-success',
                'not_interested': 'badge-danger',
                'no_answer': 'badge-warning'
            };
            return classes[status] || 'badge-secondary';
        },
        
        getStatusCardClass(status) {
            const classes = {
                'new': 'status-card-new',
                'contacted': 'status-card-contacted',
                'interested': 'status-card-interested',
                'not_interested': 'status-card-not-interested',
                'no_answer': 'status-card-no-answer'
            };
            return classes[status] || 'status-card-new';
        },
        
        getStatusIcon(status) {
            const icons = {
                'new': 'bx bx-user-plus',
                'contacted': 'bx bx-phone-call',
                'interested': 'bx bx-heart',
                'not_interested': 'bx bx-x-circle',
                'no_answer': 'bx bx-phone-off'
            };
            return icons[status] || 'bx bx-user';
        }
    }
}
</script>

<style scoped>
.cold-client-statistics {
    padding: 1rem;
}

.stat-card {
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 1.5rem;
    text-align: center;
    transition: all 0.3s ease;
}

.stat-card:hover {
    box-shadow: 0 4px 8px rgba(0,0,0,0.1);
    transform: translateY(-2px);
}

.stat-number {
    font-size: 2rem;
    font-weight: bold;
    color: #007bff;
    margin-bottom: 0.5rem;
}

.stat-label {
    color: #6c757d;
    font-size: 0.9rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
}

.status-statistics {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 12px;
    padding: 2rem;
    box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.status-card {
    background: #fff;
    border: 1px solid #e9ecef;
    border-radius: 16px;
    transition: all 0.2s ease;
    height: 100%;
    overflow: hidden;
    position: relative;
}

.status-card:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0,0,0,0.08);
}

.status-card-body {
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    height: 100%;
    min-height: 140px;
}

.status-icon-wrapper {
    width: 60px;
    height: 60px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 1rem;
    position: relative;
    color: #FFF;
    font-size: 1.5rem;
}

.status-content {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.status-label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #6c757d;
    margin-bottom: 0.5rem;
    text-transform: uppercase;
    letter-spacing: 0.8px;
    line-height: 1.2;
}

.status-count {
    font-size: 2rem;
    font-weight: 700;
    color: #2c3e50;
    line-height: 1;
}

/* Цветовые схемы для карточек */
.status-card-new {
    border-left: 4px solid #6c757d;
}

.status-card-new .status-icon-wrapper {
    background: linear-gradient(135deg, #6c757d, #5a6268);
}

.status-card-new:hover {
    border-left-color: #5a6268;
    box-shadow: 0 4px 12px rgba(108, 117, 125, 0.15);
}

.status-card-contacted {
    border-left: 4px solid #17a2b8;
}

.status-card-contacted .status-icon-wrapper {
    background: linear-gradient(135deg, #17a2b8, #138496);
}

.status-card-contacted:hover {
    border-left-color: #138496;
    box-shadow: 0 4px 12px rgba(23, 162, 184, 0.15);
}

.status-card-interested {
    border-left: 4px solid #28a745;
}

.status-card-interested .status-icon-wrapper {
    background: linear-gradient(135deg, #28a745, #1e7e34);
}

.status-card-interested:hover {
    border-left-color: #1e7e34;
    box-shadow: 0 4px 12px rgba(40, 167, 69, 0.15);
}

.status-card-not-interested {
    border-left: 4px solid #dc3545;
}

.status-card-not-interested .status-icon-wrapper {
    background: linear-gradient(135deg, #dc3545, #c82333);
}

.status-card-not-interested:hover {
    border-left-color: #c82333;
    box-shadow: 0 4px 12px rgba(220, 53, 69, 0.15);
}

.status-card-no-answer {
    border-left: 4px solid #ffc107;
}

.status-card-no-answer .status-icon-wrapper {
    background: linear-gradient(135deg, #ffc107, #e0a800);
}

.status-card-no-answer:hover {
    border-left-color: #e0a800;
    box-shadow: 0 4px 12px rgba(255, 193, 7, 0.15);
}

/* Адаптивность для мобильных устройств */
@media (max-width: 768px) {
    .status-statistics {
        padding: 1.5rem;
    }
    
    .status-card-body {
        padding: 1.25rem;
        min-height: 120px;
    }
    
    .status-icon-wrapper {
        width: 50px;
        height: 50px;
        margin-bottom: 0.75rem;
    }
    
    .status-icon {
        font-size: 1.25rem;
    }
    
    .status-count {
        font-size: 1.75rem;
    }
    
    .status-label {
        font-size: 0.7rem;
    }
}

@media (max-width: 576px) {
    .status-statistics {
        padding: 1rem;
    }
    
    .status-card-body {
        padding: 1rem;
        min-height: 100px;
    }
    
    .status-icon-wrapper {
        width: 45px;
        height: 45px;
        margin-bottom: 0.5rem;
    }
    
    .status-icon {
        font-size: 1.1rem;
    }
    
    .status-count {
        font-size: 1.5rem;
    }
    
    .status-label {
        font-size: 0.65rem;
        letter-spacing: 0.5px;
    }
}

.remaining-clients {
    background: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 6px;
    padding: 0.75rem;
    margin-top: 0.5rem;
}

.remaining-label {
    color: #856404;
    font-weight: 500;
}

.remaining-count {
    color: #856404;
    font-weight: bold;
    font-size: 1.1rem;
    margin-left: 0.5rem;
}

/* Стили для успешного сообщения */
.success-icon {
    font-size: 1.5rem;
    color: #28a745;
    margin-top: 0.25rem;
}

.success-message {
    background: linear-gradient(135deg, #d4edda, #c3e6cb);
    border: 1px solid #b8dacc;
    border-radius: 8px;
    padding: 0.75rem;
    margin-top: 0.75rem;
    color: #155724;
    font-size: 0.9rem;
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    animation: successPulse 2s ease-in-out infinite;
}

@keyframes successPulse {
    0% {
        box-shadow: 0 0 0 0 rgba(40, 167, 69, 0.4);
    }
    50% {
        box-shadow: 0 0 0 8px rgba(40, 167, 69, 0.1);
    }
    100% {
        box-shadow: 0 0 0 0 rgba(40, 167, 69, 0);
    }
}

.success-message i {
    color: #ffc107;
    font-size: 1rem;
}

.alert-success {
    border-left: 4px solid #28a745;
    background: linear-gradient(135deg, #d4edda, #f8f9fa);
}

.alert-success .alert-heading {
    color: #155724 !important;
}

.alert {
    border-radius: 8px;
}

.alert-heading {
    font-size: 1rem;
    font-weight: 600;
}

.badge-secondary {
    background-color: #6c757d;
    color: white;
}

.badge-info {
    background-color: #17a2b8;
    color: white;
}

.badge-success {
    background-color: #28a745;
    color: white;
}

.badge-danger {
    background-color: #dc3545;
    color: white;
}

.badge-warning {
    background-color: #ffc107;
    color: #212529;
}
</style>